import { zodResolver } from "@hookform/resolvers/zod";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { Button, Checkbox, Divider, Grid, Stack, TextField, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider, TimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/pl";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { z } from "zod";
import BasicDialog from "../../components/base/BasicDialog";
import { SnackbarError } from "../../components/base/SnackbarAlert/SnackbarError";
import { ClientPassportButton } from "../../components/buttons/ClientPassportButton";
import { AllDayTodoSwitches } from "../../components/form/AllDayTodoSwitches";
import { ControlledSelect } from "../../components/form/ControlledSelect/ControlledSelect";
import { ControlledTextField } from "../../components/form/ControlledTextField/ControlledTextField";
import {
    useGetTaskStatuses,
    useGetTaskSubjects,
    useGetTaskSubsubjects,
    useUpdateTask,
} from "../../services/taskService";
import { useGetSellers } from "../../services/usersApi";
import { generateOutlookCalendarLink } from "../../utils";
import { validateWorkTime } from "./CreateTaskDialog";

const editTaskFormSchema = z
    .object({
        name: z.string().min(1, { message: t("tasks.validation.nameRequired") }),
        way: z.enum(["PHONE", "EMAIL", "MEETING"]),
        priority: z.enum(["LOW", "NORMAL", "HIGH"]),
        date: z.string().optional(),
        startTime: z.string().optional(),
        stopTime: z.string().optional(),
        note: z.string().optional(),
        subject: z.string().min(1, { message: t("tasks.validation.subjectRequired") }),
        subsubject: z.string().optional(),
        seller: z.string().min(1, { message: t("tasks.validation.sellerRequired") }),
        status: z.string().optional(),
    })
    .refine(
        (data) => {
            // If start time is provided, stop time must also be provided
            console.log("Validating times", data.startTime, data.stopTime);
            if (data.startTime && !data.stopTime) {
                return false;
            }
            return true;
        },
        {
            message: t("tasks.validation.bothTimesRequired"),
            path: ["stopTime"],
        },
    )
    .refine(
        (data) => {
            // If stop time is provided, start time must also be provided
            if (!data.startTime && data.stopTime) {
                return false;
            }
            return true;
        },
        {
            message: t("tasks.validation.bothTimesRequired"),
            path: ["startTime"],
        },
    )
    .refine((data) => validateWorkTime(data.startTime), {
        message: t("tasks.validation.timeOutsideBusinessHours"),
        path: ["startTime"],
    })
    .refine((data) => validateWorkTime(data.stopTime), {
        message: t("tasks.validation.timeOutsideBusinessHours"),
        path: ["stopTime"],
    })
    .refine(
        (data) => {
            // If both times are set, ensure stop is after start
            if (data.startTime && data.stopTime) {
                return data.stopTime > data.startTime;
            }
            return true;
        },
        {
            message: t("tasks.validation.stopAfterStart"),
            path: ["stopTime"],
        },
    );

export function EditTaskDialog({ task, onClose, handleEditTask }) {
    const [isNameValueSet, setIsNameValueSet] = useState(false);
    const [addToCalendar, setAddToCalendar] = useState(false);
    const [isAllDay, setIsAllDay] = useState(!!task.is_allday);
    const [isTodo, setIsTodo] = useState(!task.start);

    // Set dayjs locale to Polish
    dayjs.locale("pl");

    // Parse existing task data for form defaults
    const getDateFromISOString = (isoString) => {
        if (!isoString) {
            return "";
        }
        return dayjs(isoString).format("YYYY-MM-DD");
    };

    const getTimeFromISOString = (isoString) => {
        if (!isoString) {
            return "";
        }
        const time = dayjs(isoString).format("HH:mm");
        return time === "00:00" ? "" : time; // Don't show 00:00 for all-day tasks
    };

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: zodResolver(editTaskFormSchema),
        defaultValues: {
            name: task.name,
            way: task.way,
            priority: task.priority,
            date: getDateFromISOString(task.start),
            startTime: getTimeFromISOString(task.start),
            stopTime: !isAllDay ? getTimeFromISOString(task.stop) : "",
            note: task.note,
            subject: task.subject.id,
            subsubject: task.subsubject?.id || "",
            seller: task.seller.id,
            status: "",
        },
    });

    const { data: statuses } = useGetTaskStatuses({
        select: (data) =>
            data.results.map((status) => ({
                label: t(`tasks.statuses.${status.status_name_eng}`),
                value: status.id,
            })),
    });

    useEffect(() => {
        if (statuses && statuses.length > 1 && !watch("status")) {
            setValue("status", statuses[1].value);
        }
    }, [statuses, setValue, watch]);

    const selectedSubject = watch("subject");
    const { data: subjects } = useGetTaskSubjects({
        select: (data) =>
            data.results.map((subject) => ({
                label: subject.name,
                value: subject.id,
            })),
    });

    const { data: subsubjects } = useGetTaskSubsubjects(selectedSubject, {
        select: (data) =>
            data.map((subsubject) => ({
                label: subsubject.name,
                value: subsubject.id,
            })),
        enabled: !!selectedSubject,
    });

    const { data: sellers } = useGetSellers({
        select: (data) =>
            data.map((seller) => ({
                label: seller.full_name,
                value: seller.id,
            })),
    });

    const { mutate: updateTask, status } = useUpdateTask({
        onSuccess: () => {
            toast.success(t("tasks.alerts.updateSuccess"));
            onClose();
        },
        onError: (error) => {
            console.error(error);
            toast.custom(() => <SnackbarError message={t("tasks.updateError")} />);
        },
    });

    const onSubmit = (data) => {
        let startDate = null;
        let stopDate = null;

        if (!isTodo && data.date) {
            if (isAllDay) {
                startDate = dayjs(data.date).startOf("day").toDate().toISOString();
                stopDate = dayjs(data.date).endOf("day").toDate().toISOString();
            } else if (data.startTime && data.stopTime) {
                const startDateTime = dayjs(`${data.date}T${data.startTime}`);
                const stopDateTime = dayjs(`${data.date}T${data.stopTime}`);
                startDate = startDateTime.toDate().toISOString();
                stopDate = stopDateTime.toDate().toISOString();
            }
        }

        const payload = {
            name: data.name,
            way: data.way,
            priority: data.priority,
            start: startDate,
            stop: stopDate,
            note: data.note,
            subject: data.subject,
            subsubject: data.subsubject || undefined,
            seller: data.seller,
            status: data.status,
            is_allday: isAllDay,
        };

        if (handleEditTask) {
            handleEditTask(payload)
        }

        updateTask({ task, payload });

        // If checkbox is selected, open Outlook calendar link in new tab
        if (addToCalendar) {
            const calendarUrl = generateOutlookCalendarLink(data, {
                customer: task.customer?.person ? task.customer.person : undefined,
                subjects,
                isAllDay: isAllDay,
                subsubjects,
                getTranslation: t,
            });
            window.open(calendarUrl, "_blank");
        }
    };

    const enabledBtn = watch(["name", "subject"]).every(Boolean);

    // Add a useEffect to watch for subject and subsubject changes and update the name field
    useEffect(() => {
        if (!selectedSubject) {
            return;
        }

        // Check if name is empty
        // const currentName = watch("name");
        // if (currentName && currentName.trim() !== "") {
        //     return;
        // }

        // Find the selected subject and subsubject objects
        const selectedSubjectObj = subjects?.find((s) => s.value === selectedSubject);
        const selectedSubsubjectObj = subsubjects?.find((ss) => ss.value === watch("subsubject"));

        // If we select subject, construct the name
        if (selectedSubjectObj) {
            let generatedName = `${selectedSubjectObj.label}`
            if (selectedSubsubjectObj) {
                generatedName += ` - ${selectedSubsubjectObj.label}`
            };
            setValue("name", generatedName);
            setIsNameValueSet(true);
        }
    }, [watch("subsubject"), subjects, subsubjects, selectedSubject, setValue]);

    return (
        <BasicDialog
            open={true}
            onClose={onClose}
            titleAlign="center"
            contentAlign="center"
            title={t("tasks.editTaskDialog.title")}
            maxWidth="md"
            showDialogActions={false}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography sx={{ mb: 2, fontSize: 18 }}>
                            {t("tasks.editTaskDialog.customerSection")}
                        </Typography>
                        {task.customer ? (
                            <Stack direction="row" spacing={2} alignItems="center" width="100%">
                                <TextField
                                    value={
                                        task.customer.company
                                            ? task.customer.company.name_short
                                            : task.customer.person?.name
                                    }
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    label={
                                        task.customer.company
                                            ? t("tasks.editTaskDialog.companyName")
                                            : t("tasks.editTaskDialog.customerName")
                                    }
                                />
                                <TextField
                                    value={
                                        task.customer.company
                                            ? task.customer.company.nip
                                            : task.customer.person?.surname
                                    }
                                    InputProps={{ readOnly: true }}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    label={
                                        task.customer.company
                                            ? t("tasks.editTaskDialog.nip")
                                            : t("tasks.editTaskDialog.customerSurname")
                                    }
                                />
                                <ClientPassportButton
                                    clientId={task.customer.id}
                                    clientType={task.customer.company ? "company" : "customer"}
                                />
                            </Stack>
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                {t("tasks.editTaskDialog.noCustomer")}
                            </Typography>
                        )}
                    </Grid>

                    <Grid item xs={12}>
                        <Divider />
                    </Grid>

                    {/* Task Details */}
                    <Grid item xs={12}>
                        <Typography sx={{ fontSize: 18 }}>
                            {t("tasks.editTaskDialog.taskDetails")}
                        </Typography>
                    </Grid>

                    {/* Task Name */}
                    <Grid item xs={12}>
                        <ControlledTextField
                            control={control}
                            name="name"
                            label={`${t("tasks.editTaskDialog.name")}*`}
                            props={{
                                error: !!errors.name,
                                helperText: errors.name?.message,
                                InputLabelProps: {
                                    shrink: isNameValueSet || !!watch("name"),
                                },
                            }}
                        />
                    </Grid>

                    {/* Subject and Subsubject */}
                    <Grid item xs={6}>
                        <ControlledSelect
                            control={control}
                            name="subject"
                            label={`${t("tasks.editTaskDialog.subject")}*`}
                            data={subjects || []}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <ControlledSelect
                            control={control}
                            name="subsubject"
                            label={t("tasks.editTaskDialog.subsubject")}
                            data={subsubjects || []}
                            disabled={!selectedSubject || subsubjects?.length === 0}
                        />
                    </Grid>

                    {/* All Day / TODO Checkboxes */}
                    <AllDayTodoSwitches
                        isAllDay={isAllDay}
                        setIsAllDay={setIsAllDay}
                        isTodo={isTodo}
                        setIsTodo={setIsTodo}
                        setValue={setValue}
                        t={t}
                    />

                    {/* Date and Times */}
                    <Grid item xs={4}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pl">
                            <Controller
                                control={control}
                                name="date"
                                render={({ field }) => (
                                    <DatePicker
                                        label={`${t("tasks.editTaskDialog.date")}`}
                                        value={field.value ? dayjs(field.value) : null}
                                        onChange={(date) => {
                                            field.onChange(date ? date.format("YYYY-MM-DD") : "");
                                        }}
                                        disabled={isTodo}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                error={!!errors.date}
                                                helperText={errors.date?.message}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={4}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pl">
                            <Controller
                                control={control}
                                name="startTime"
                                render={({ field }) => (
                                    <TimePicker
                                        label={t("tasks.editTaskDialog.startTime")}
                                        value={
                                            field.value ? dayjs(`2000-01-01T${field.value}`) : null
                                        }
                                        onChange={(time) => {
                                            const timeString = time?.format("HH:mm") || "";
                                            field.onChange(timeString);

                                            // If stop time is earlier than new start time, update stop time
                                            const stopTime = watch("stopTime");
                                            if (stopTime && timeString && stopTime < timeString) {
                                                setValue("stopTime", timeString);
                                            }
                                        }}
                                        disabled={isAllDay || isTodo}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                error={!!errors.startTime}
                                                helperText={
                                                    errors.startTime?.message ||
                                                    t("tasks.addTaskDialog.timeOptional")
                                                }
                                            />
                                        )}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={4}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pl">
                            <Controller
                                control={control}
                                name="stopTime"
                                render={({ field }) => (
                                    <TimePicker
                                        label={t("tasks.editTaskDialog.stopTime")}
                                        value={
                                            field.value ? dayjs(`2000-01-01T${field.value}`) : null
                                        }
                                        onChange={(time) => {
                                            const timeString = time?.format("HH:mm") || "";
                                            field.onChange(timeString);
                                        }}
                                        minTime={
                                            watch("startTime")
                                                ? dayjs(`2000-01-01T${watch("startTime")}`)
                                                : undefined
                                        }
                                        disabled={isAllDay || isTodo}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                fullWidth
                                                error={!!errors.stopTime}
                                                helperText={
                                                    errors.stopTime?.message ||
                                                    t("tasks.editTaskDialog.timeOptional")
                                                }
                                            />
                                        )}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Grid>

                    {/* Status */}
                    <Grid item xs={4}>
                        <ControlledSelect
                            control={control}
                            name="status"
                            label={t("tasks.editTaskDialog.status")}
                            data={statuses || []}
                        />
                    </Grid>

                    {/* Way */}
                    <Grid item xs={4}>
                        <ControlledSelect
                            control={control}
                            name="way"
                            label={`${t("tasks.editTaskDialog.way")}*`}
                            data={[
                                { value: "PHONE", label: t("tasks.ways.PHONE") },
                                { value: "EMAIL", label: t("tasks.ways.EMAIL") },
                                { value: "MEETING", label: t("tasks.ways.MEETING") },
                            ]}
                        />
                    </Grid>

                    {/* Priority */}
                    <Grid item xs={4}>
                        <ControlledSelect
                            control={control}
                            name="priority"
                            label={`${t("tasks.editTaskDialog.priority")}*`}
                            data={[
                                { value: "LOW", label: t("tasks.priorities.LOW") },
                                { value: "NORMAL", label: t("tasks.priorities.NORMAL") },
                                { value: "HIGH", label: t("tasks.priorities.HIGH") },
                            ]}
                        />
                    </Grid>

                    {/* Seller */}
                    <Grid item xs={12}>
                        <ControlledSelect
                            control={control}
                            name="seller"
                            label={`${t("tasks.editTaskDialog.seller")}*`}
                            data={sellers || []}
                        />
                    </Grid>

                    {/* Note */}
                    <Grid item xs={12}>
                        <ControlledTextField
                            control={control}
                            name="note"
                            label={`${t("tasks.editTaskDialog.note")}`}
                            props={{
                                InputProps: {
                                    rows: 3,
                                    multiline: true,
                                },
                            }}
                        />
                    </Grid>

                    {/* Buttons */}
                    <Grid
                        item
                        xs={12}
                        container
                        justifyContent="center"
                        spacing={2}
                        alignItems="center"
                    >
                        <Grid item xs>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                justifyContent="flex-start"
                            >
                                <Checkbox
                                    checked={addToCalendar}
                                    onChange={(e) => setAddToCalendar(e.target.checked)}
                                />
                                <CalendarTodayIcon color={addToCalendar ? "primary" : "action"} />
                            </Stack>
                        </Grid>
                        <Grid item>
                            <Button type="button" variant="outlined" onClick={onClose}>
                                {t("tasks.editTaskDialog.cancel")}
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button type="submit" variant="contained" disabled={!enabledBtn}>
                                {t("tasks.editTaskDialog.saveChanges")}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </form>
        </BasicDialog>
    );
}
